/**
 * Single source of truth for client-side file upload constraints.
 *
 * Image uploads MUST stay in sync with backend Assert\Image on App\Entity\MediaObject::$file
 * (group media_object_create). Route document uploads MUST stay in sync with Assert\File
 * (group media_object_document_create) and POST /api/media_objects/documents.
 */

export const ALLOWED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp']

export const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp']

export const ALLOWED_IMAGE_FORMATS_LABEL = 'JPG, PNG, WEBP'

export const MAX_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024

export const MAX_UPLOAD_SIZE_LABEL = '10 MB'

/** Route document attachment: PDF, DOC, DOCX (must match backend MediaObject document upload). */
export const ALLOWED_ROUTE_DOCUMENT_MIME_TYPES = [
	'application/pdf',
	'application/msword',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
]

export const ALLOWED_ROUTE_DOCUMENT_EXTENSIONS = ['.pdf', '.doc', '.docx']

export const ALLOWED_ROUTE_DOCUMENT_FORMATS_LABEL = 'PDF, DOC, DOCX'

export const ROUTE_DOCUMENT_UPLOAD_ACCEPT_ATTRIBUTE = [
	...ALLOWED_ROUTE_DOCUMENT_MIME_TYPES,
	'.pdf',
	'.doc',
	'.docx'
].join(',')

export const ROUTE_DOCUMENT_UPLOAD_HINT_FORMATS_LINE = `Dozwolone formaty: ${ALLOWED_ROUTE_DOCUMENT_FORMATS_LABEL}`

export const ROUTE_DOCUMENT_UPLOAD_HINT_MAX_SIZE_LINE = `Maksymalny rozmiar: ${MAX_UPLOAD_SIZE_LABEL}`

/** @deprecated Prefer ROUTE_DOCUMENT_UPLOAD_HINT_FORMATS_LINE + ROUTE_DOCUMENT_UPLOAD_HINT_MAX_SIZE_LINE in UI (two lines). */
export const ROUTE_DOCUMENT_UPLOAD_HINT_TEXT = `${ROUTE_DOCUMENT_UPLOAD_HINT_FORMATS_LINE}\n${ROUTE_DOCUMENT_UPLOAD_HINT_MAX_SIZE_LINE}`

/**
 * Use for <b-upload accept="..."> / <input accept="..."> attribute.
 * Browsers do filter by this on file picker, but we still validate server-side
 * because the attribute is trivially bypassable.
 */
export const UPLOAD_ACCEPT_ATTRIBUTE = ALLOWED_IMAGE_MIME_TYPES.join(',')

export const UPLOAD_HINT_TEXT = `Dozwolone formaty: ${ALLOWED_IMAGE_FORMATS_LABEL} · max ${MAX_UPLOAD_SIZE_LABEL}`

// Buefy's toast renders `message` via v-html, and we interpolate file.name into
// these messages. Escape it so e.g. "<weird>.png" can't slip raw markup through.
function escapeHtml(value) {
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}

/**
 * @param {File} file
 * @returns {{ valid: true } | { valid: false, message: string }}
 */
export function validateImageFile(file) {
	if (!file) {
		return { valid: false, message: 'Brak pliku do wgrania.' }
	}

	const safeName = escapeHtml(file.name || '')
	const mime = (file.type || '').toLowerCase()
	if (mime && !ALLOWED_IMAGE_MIME_TYPES.includes(mime)) {
		return {
			valid: false,
			message: `Plik "${safeName}" ma niedozwolony format (${escapeHtml(mime)}). Dozwolone: ${ALLOWED_IMAGE_FORMATS_LABEL}.`
		}
	}

	if (!mime) {
		const lowerName = (file.name || '').toLowerCase()
		const matchesExtension = ALLOWED_IMAGE_EXTENSIONS.some(ext => lowerName.endsWith(ext))
		if (!matchesExtension) {
			return {
				valid: false,
				message: `Plik "${safeName}" ma niedozwolone rozszerzenie. Dozwolone: ${ALLOWED_IMAGE_FORMATS_LABEL}.`
			}
		}
	}

	if (typeof file.size === 'number' && file.size > MAX_UPLOAD_SIZE_BYTES) {
		const sizeMb = (file.size / (1024 * 1024)).toFixed(1)
		return {
			valid: false,
			message: `Plik "${safeName}" jest za duży (${sizeMb} MB). Maksymalny rozmiar to ${MAX_UPLOAD_SIZE_LABEL}.`
		}
	}

	return { valid: true }
}

/**
 * @param {File} file
 * @returns {{ valid: true } | { valid: false, message: string }}
 */
export function validateRouteDocumentFile(file) {
	if (!file) {
		return { valid: false, message: 'Brak pliku do wgrania.' }
	}

	const safeName = escapeHtml(file.name || '')
	const mime = (file.type || '').toLowerCase()
	if (mime && !ALLOWED_ROUTE_DOCUMENT_MIME_TYPES.includes(mime)) {
		return {
			valid: false,
			message: `Plik "${safeName}" ma niedozwolony format (${escapeHtml(mime)}). Dozwolone: ${ALLOWED_ROUTE_DOCUMENT_FORMATS_LABEL}.`
		}
	}

	if (!mime) {
		const lowerName = (file.name || '').toLowerCase()
		const matchesExtension = ALLOWED_ROUTE_DOCUMENT_EXTENSIONS.some(ext => lowerName.endsWith(ext))
		if (!matchesExtension) {
			return {
				valid: false,
				message: `Plik "${safeName}" ma niedozwolone rozszerzenie. Dozwolone: ${ALLOWED_ROUTE_DOCUMENT_FORMATS_LABEL}.`
			}
		}
	}

	if (typeof file.size === 'number' && file.size > MAX_UPLOAD_SIZE_BYTES) {
		const sizeMb = (file.size / (1024 * 1024)).toFixed(1)
		return {
			valid: false,
			message: `Plik "${safeName}" jest za duży (${sizeMb} MB). Maksymalny rozmiar to ${MAX_UPLOAD_SIZE_LABEL}.`
		}
	}

	return { valid: true }
}

/**
 * Pulls a human-readable error message out of an axios error originating from
 * the /api/media_objects upload endpoint. Handles API Platform's 422
 * ValidationException payload (violations[]), generic detail/message fields,
 * and falls back to a sensible default.
 *
 * @param {unknown} error axios error or anything thrown by a network layer
 * @returns {string}
 */
export function extractUploadErrorMessage(error) {
	const response = error && error.response
	const data = response && response.data

	if (data && Array.isArray(data.violations) && data.violations.length) {
		const firstWithMessage = data.violations.find(v => v && typeof v.message === 'string' && v.message)
		if (firstWithMessage) {
			return firstWithMessage.message
		}
	}

	if (data && typeof data === 'object') {
		if (typeof data.detail === 'string' && data.detail) return data.detail
		if (typeof data.description === 'string' && data.description) return data.description
		if (typeof data.message === 'string' && data.message) return data.message
		if (typeof data['hydra:description'] === 'string' && data['hydra:description']) return data['hydra:description']
	}

	if (response && response.status === 413) {
		return `Plik jest za duży. Maksymalny rozmiar to ${MAX_UPLOAD_SIZE_LABEL}.`
	}

	if (error && typeof error.message === 'string' && error.message) {
		return error.message
	}

	return 'Wystąpił błąd i załącznik nie został zapisany.'
}
